import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input, Modal, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Eye, User, Briefcase, Mail, Phone, MapPin, Calendar, Octagon } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

export default function ViewEmployees() {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");

    // Modal State
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('1');

    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const prevSearch = useRef(searchTerm);

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return; // Exit early, let page change trigger next fetch
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };
            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("users/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total);
            } else if (Array.isArray(response)) {
                setData(response);
                setTotalRows(response.length);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_employee`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_employee`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("users/delete", { id: id });
                    toast.success("Employee deleted successfully", { autoClose: 1000 });
                    fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
                } catch (error) {
                    toast.error("Failed to delete employee", { autoClose: 1000 });
                }
            }
        });
    };

    // Modal Helpers
    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550); // Slightly less than 700ms to ensure smooth transition
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }
    const handleView = (row: any) => {
        setSelectedEmployee(row);
        setActiveTab('1');
        setViewModal(true);
    };


    const columns = [
        {
            name: <div>Sl.No</div>,
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: <div>Name</div>,
            selector: (row: any) => row.name,
            sortable: true,
            wrap: true,
            sortField: "name",
            cell: (row: any) => (
                <div className="d-flex align-items-center">
                    {row.profile_image ? (
                        <img
                            src={`${mediaUrl}${row.profile_image}`}
                            alt={row.name}
                            style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '10px', objectFit: 'cover' }}
                        />
                    ) : (
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#eee', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <User size={14} className="text-muted" />
                        </div>
                    )}
                    <span className="fw-bold">{row.name}</span>
                </div>
            )
        },
        {
            name: <div>Email</div>,
            selector: (row: any) => row.email,
            sortable: true,
            sortField: "email",
            wrap: true,
        },
        {
            name: <div>Phone</div>,
            selector: (row: any) => row.phone || "N/A",
            sortable: true,
            sortField: "phone", wrap: true,
        },
        {
            name: <div>Designation</div>,
            selector: (row: any) => row.designation || "N/A",
            sortable: true,
            sortField: "designation", wrap: true,
        },
        {
            name: "Role",
            selector: (row: any) => row.role ? row.role.name : "N/A",
            sortable: true,
            sortField: "role", wrap: true,
            cell: (row: any) => <span className="text-primary fw-semibold">{row.role ? row.role.name : "N/A"}</span>
        },
        {
            name: "Status",
            selector: (row: any) => row.status || "Active",
            sortable: true,
            sortField: "status",
            wrap: true,
            cell: (row: any) => (
                <Badge color={row.status === 'Inactive' ? 'danger' : 'success'}>
                    {row.status || 'Active'}
                </Badge>
            )
        },
        {
            name: "Actions",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('user-management.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('user-management.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('user-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "160px"
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Employee Management" parent="Master Settings" title="Employee Management" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Employees Overview</H3>
                                {hasPermission('user-management.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Employee
                                    </Btn>
                                )}
                            </Col>
                            <Col xs={12} className="mb-3 d-flex justify-content-end">
                                <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                            </Col>
                            <div className="table-responsive">
                                <DataTable
                                    data={data}
                                    columns={columns}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    onChangePage={handlePageChange}
                                    onSort={handleSort}
                                    sortServer
                                    progressPending={loading}
                                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                    highlightOnHover
                                    customStyles={customStyles}
                                />
                            </div>

                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View Employee Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                {selectedEmployee && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',// Sky/Blue gradient
                        padding: '1.5rem',
                        position: 'relative', width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={handleClose}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        ></button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0,
                                overflow: 'hidden',
                                border: '3px solid rgba(255,255,255,0.5)'
                            }}>
                                {selectedEmployee.profile_image ? (
                                    <img src={`${mediaUrl}${selectedEmployee.profile_image}`} alt={selectedEmployee.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <User size={40} color="#0ea5e9" />
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.8rem',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                }}>
                                    {selectedEmployee.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.9)', fontSize: '1rem' }}>
                                    <span style={{ fontWeight: 600 }}>{selectedEmployee.designation || 'No Designation'}</span>
                                    <span>•</span>
                                    <span>{selectedEmployee.role?.name || 'No Role'}</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: selectedEmployee.status === 'Active' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                                    color: 'white',
                                    padding: '0.5rem 1rem',
                                    margin: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    {selectedEmployee.status || 'Active'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <User size={16} /> Personal Info
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Briefcase size={16} /> Employment Details
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedEmployee && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <Mail size={14} /> Email Address
                                                    </label>
                                                    <div className="fw-semibold fs-6">{selectedEmployee.email}</div>
                                                </div>
                                                <div>
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <Phone size={14} /> Phone Number
                                                    </label>
                                                    <div className="fw-semibold fs-6">{selectedEmployee.phone || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div>
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <MapPin size={14} /> Address
                                                    </label>
                                                    <div className="fw-semibold fs-6 p-3 bg-light rounded text-dark" style={{ minHeight: '80px' }}>
                                                        {selectedEmployee.address || 'No address provided'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <Briefcase size={14} /> Job Designation
                                                    </label>
                                                    <div className="fw-bold fs-5 text-primary">{selectedEmployee.designation || 'N/A'}</div>
                                                </div>
                                                <div>
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <Octagon size={14} /> Assigned Role
                                                    </label>
                                                    <Badge color="info" className="p-2 fs-6">
                                                        {selectedEmployee.role?.name || 'No Role'}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <Calendar size={14} /> Date of Joining
                                                    </label>
                                                    <div className="fw-semibold fs-6">
                                                        {selectedEmployee.joining_date ? new Date(selectedEmployee.joining_date).toLocaleDateString('en-GB') : 'N/A'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-2 mb-2">
                                                        <User size={14} /> Account Status
                                                    </label>
                                                    <div className={`fw-bold ${selectedEmployee.status === 'Inactive' ? 'text-danger' : 'text-success'}`}>
                                                        {selectedEmployee.status || 'Active'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}
